// RmaRequestForm — the BODY of the "Request a return" dialog for hardware
// products (My Account C1, task T7b, design.md AC5/AC6).
//
// ⚠️ AMENDED 2026-10-04 (account-area redesign, Task 7, Amendment
// 2026-10-02 F6). ORIGINAL: this file was `RmaRequestControl`, the
// renderProductActionSlot content — an inline "Request Return" button plus
// an expandable inline <form>, with the open-request gate rendering
// "Return {status}" / "Last request {status}" text in the slot. CURRENT:
// the amendment REPLACED that slot (rule-48 note on ProductsTabProps) — the
// ⋯ row menu carries the single "Request a return" item (hardware only) and
// ProductsPanel opens this form inside an EmbedDialog; the open-request
// gate HIDES the ⋯ menu entirely and the live status renders as the Status
// column's rma badge (status.ts domain="rma"), so the slot's gate text is
// retired with the slot. The form itself — fields, copy, the verbatim 409
// contract — is carried over unchanged from the inline version.
//
// Presentational state (field values, submit-in-flight, error) lives here;
// the resolved order_line_id is passed in (ProductsPanel owns that data —
// a second fetch was once needed to get order_line_id at all; the owned
// item's `lines` now carry it). The host dialog's open state belongs
// to ProductsPanel: the form calls onClose() on success and on Cancel and
// never unmounts itself.
//
// Duplicate-open (409 RMA_OPEN_REQUEST_EXISTS) is surfaced verbatim via the
// thrown ApiError's message and NEVER retried (this task's brief, verbatim).
//
// Inline styles only — no Tailwind. Every <button> is explicitly
// type="button" or type="submit" (project rule §73's spirit — this is a
// consumer of EmbedButton, not a new primitive, but the same discipline
// applies: the Cancel button sits inside this component's own <form> and
// MUST NOT submit it).

import { useState, useCallback, type FormEvent } from "react";
import {
  useEmbedTokens,
  EmbedButton,
  EmbedBanner,
  EmbedField,
  EmbedInput,
  EmbedSelect,
  EmbedTextarea,
} from "../../embed-shared/primitives";
import { ApiError } from "../../embed-shared/api-client";
import { formatInstantDate } from "../../embed-shared/format";
import { createRMARequest, type RMARequestWire } from "./rma-api";
import type { OwnedLineWire } from "./owned-products-api";

/** Lines a return can be filed against (R8: units are not owned until
 *  fulfilled): only `shipped` / `delivered` lines with something left to return
 *  (quantity is net of completed returns). Order is kept — the server serves
 *  them newest first. */
export function returnableLines(lines: OwnedLineWire[]): OwnedLineWire[] {
  return lines.filter((l) => (l.status === "shipped" || l.status === "delivered") && l.quantity > 0);
}

export interface RmaRequestFormProps {
  productId: string;
  /** The order line to submit against, resolved by ProductsPanel from
   *  the owned item's lines. Undefined means we couldn't resolve one (should
   *  not normally happen — the row only exists because of an owned
   *  non-cancelled line — but is handled defensively). */
  orderLineId: string | undefined;
  /** TD-671 Task 8: the owned item's returnable lines (newest first), from
   *  GET /me/owned-products. When present it REPLACES orderLineId /
   *  maxQuantity: one line is used as-is; several render a "Purchase" select
   *  (newest selected) and the chosen line's `quantity` becomes the cap. */
  lines?: OwnedLineWire[];
  /** The line's quantity. The quantity input renders only when this is > 1
   *  (TD-671 R7, partial returns); undefined/1 sends no quantity at all. */
  maxQuantity?: number;
  onCreated: (created: RMARequestWire) => void;
  /** Close the host dialog (ProductsPanel owns the open state): called on
   *  successful create and on Cancel. */
  onClose: () => void;
}

export function RmaRequestForm({
  productId,
  orderLineId: orderLineIdProp,
  lines,
  maxQuantity: maxQuantityProp,
  onCreated,
  onClose,
}: RmaRequestFormProps) {
  const t = useEmbedTokens();
  const [chosenLineId, setChosenLineId] = useState<string | undefined>(lines?.[0]?.order_line_id);
  const chosenLine = lines ? (lines.find((l) => l.order_line_id === chosenLineId) ?? lines[0]) : undefined;
  const orderLineId = lines ? chosenLine?.order_line_id : orderLineIdProp;
  const maxQuantity = lines ? chosenLine?.quantity : maxQuantityProp;
  const [reason, setReason] = useState("");
  const [note, setNote] = useState("");
  const showQuantity = maxQuantity !== undefined && maxQuantity > 1;
  const [quantity, setQuantity] = useState(String(maxQuantity ?? 1));
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleCancel = useCallback(() => {
    setReason("");
    setNote("");
    setQuantity(String(maxQuantity ?? 1));
    setError(null);
    onClose();
  }, [onClose, maxQuantity]);

  const handleSubmit = useCallback(
    async (e: FormEvent) => {
      e.preventDefault();
      if (!reason.trim()) {
        setError("Please tell us why you're returning this item.");
        return;
      }
      if (!orderLineId) {
        setError("We couldn't find a purchase to attach this request to.");
        return;
      }
      // The default (every unit) posts no quantity: absent means "all
      // remaining" on the server, which stays correct if a sibling request
      // already returned some units.
      let returnQuantity: number | undefined;
      if (showQuantity) {
        const n = Number(quantity);
        if (quantity.trim() === "" || !Number.isInteger(n) || n < 1 || n > maxQuantity) {
          setError(`Enter a quantity between 1 and ${maxQuantity}.`);
          return;
        }
        if (n !== maxQuantity) returnQuantity = n;
      }
      setSubmitting(true);
      setError(null);
      try {
        const created = await createRMARequest({
          order_line_id: orderLineId,
          reason: reason.trim(),
          note: note.trim() || undefined,
          ...(returnQuantity !== undefined ? { quantity: returnQuantity } : {}),
        });
        onCreated(created);
        setReason("");
        setNote("");
        setQuantity(String(maxQuantity ?? 1));
        onClose();
      } catch (err) {
        // 409 RMA_OPEN_REQUEST_EXISTS lands here too — the server message is
        // already customer-clear ("An open RMA request already exists for
        // this order line"); surface it verbatim, never auto-retry.
        setError(err instanceof ApiError ? err.message : "Failed to submit return request.");
      } finally {
        setSubmitting(false);
      }
    },
    [orderLineId, reason, note, quantity, showQuantity, maxQuantity, onCreated, onClose],
  );

  return (
    <form onSubmit={handleSubmit} noValidate>
      {error && (
        <div style={{ marginBottom: t.spacing.xs }}>
          <EmbedBanner tone="error">{error}</EmbedBanner>
        </div>
      )}
      <EmbedField label="Reason" htmlFor={`rma-reason-${productId}`} required>
        <EmbedInput
          id={`rma-reason-${productId}`}
          type="text"
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          aria-required="true"
        />
      </EmbedField>
      {lines && lines.length > 1 && (
        <EmbedField label="Purchase" htmlFor={`rma-line-${productId}`}>
          <EmbedSelect
            id={`rma-line-${productId}`}
            value={chosenLine?.order_line_id ?? ""}
            onChange={(e) => {
              const next = lines.find((l) => l.order_line_id === e.target.value);
              setChosenLineId(e.target.value);
              // A new line has a new cap: restart from "every unit" of it.
              setQuantity(String(next?.quantity ?? 1));
            }}
          >
            {lines.map((l) => (
              <option key={l.order_line_id} value={l.order_line_id}>
                {`Order ${l.order_number} · ${formatInstantDate(l.placed_at, { withYear: true })} · ${l.quantity} ${l.quantity === 1 ? "unit" : "units"}`}
              </option>
            ))}
          </EmbedSelect>
        </EmbedField>
      )}
      {showQuantity && (
        <EmbedField label="Quantity to return" htmlFor={`rma-quantity-${productId}`}>
          <EmbedInput
            id={`rma-quantity-${productId}`}
            type="number"
            inputMode="numeric"
            min={1}
            max={maxQuantity}
            step={1}
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
          />
        </EmbedField>
      )}
      <EmbedField label="Note (optional)" htmlFor={`rma-note-${productId}`}>
        <EmbedTextarea
          id={`rma-note-${productId}`}
          value={note}
          onChange={(e) => setNote(e.target.value)}
        />
      </EmbedField>
      <div style={{ display: "flex", gap: t.spacing.sm }}>
        <EmbedButton type="submit" variant="primary" size="sm" disabled={submitting} loading={submitting}>
          {submitting ? "Submitting..." : "Submit request"}
        </EmbedButton>
        <EmbedButton type="button" variant="secondary" size="sm" onClick={handleCancel} disabled={submitting}>
          Cancel
        </EmbedButton>
      </div>
    </form>
  );
}
